/* © Facilogi. See COPYRIGHT file for full copyright & licensing details. */

import Feathers from "@feathersjs/feathers";

import { Autobind } from "@Internal/Utils";
import { CustomError } from "@Internal/Models";

import { SubscriptionAudit } from "App/Models";

import { ProcessException } from "./Base";

/* Read-side for the subscription change history. The backend returns plain JSON audit rows
   (Action, Field, OldValue, NewValue, Actor, Date), consumed as-is. */
export default class SubscriptionAuditService {
    private findService: Feathers.Service<any>;

    constructor( findService: Feathers.Service<any> ) {
        this.findService = findService;
    }

    @Autobind
    async FindBySubscription(
        subscriptionId: number
    ): Promise<[ SubscriptionAudit[], CustomError | undefined ]> {
        return this.find( { SubscriptionId: subscriptionId } );
    }

    /* Whole-customer history: every subscription of the customer, including ones since deleted. */
    @Autobind
    async FindByCustomer(
        customerId: number
    ): Promise<[ SubscriptionAudit[], CustomError | undefined ]> {
        return this.find( { CustomerId: customerId } );
    }

    private async find(
        query: Record<string, number>
    ): Promise<[ SubscriptionAudit[], CustomError | undefined ]> {
        try {
            const results = await this.findService.find( { query } );

            /* The global serialization after-hook reshapes `{ Records, PagerSorter }` into
               `{ Items, Pagination }`, so read `Items` (keep `Records` as a defensive fallback). */
            const records = ( results !== undefined && results.Items !== undefined )
                ? results.Items
                : ( results !== undefined && results.Records !== undefined )
                    ? results.Records
                    : [];

            return [ records as SubscriptionAudit[], undefined ];

        } catch( error ) {
            return [ [], await ProcessException( error ) ];
        }
    }
}
